// PdfPreviewSheet — show a quote or invoice PDF inside the app (UX-085).
//
// "Preview PDF" used to open the file in a new tab. Pop-up rules, a phone's
// tab switcher and headless browsers all lost that tab, so pressing the
// button looked like nothing happened. The preview now opens in a sheet over
// the record:
//   - The PDF is fetched with the session (apiClient, cookies + CSRF) and
//     shown from a same-origin blob: URL, so it works whether or not the API
//     shares the app's cookie domain.
//   - Desktop browsers with a built-in PDF viewer show it inline (iframe).
//   - Phones can't: iOS Safari renders only the first page of an embedded PDF
//     as a flat image, Android Chrome renders nothing, and Chrome's viewer in
//     a 390px frame is only its toolbar and thumbnail rail. Phones (and any
//     narrow screen) get every page drawn as a fit-to-width image with pdf.js
//     (PdfPageImages), plus "Open PDF" (the phone's own viewer, with share
//     and print) and "Download PDF".
//   - If the pages can't be drawn, the sheet falls back to the file's name
//     with Open PDF and Download. A failed load says so with Try again.

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AlertCircle, Download, ExternalLink, FileText } from 'lucide-react'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { Spinner } from '@/components/ui/Spinner'
import { describeError } from '@/lib/errors'
import { logger } from '@/lib/logger'
import { PdfPageImages } from './PdfPageImages'

export interface PdfPreviewSheetProps {
  open: boolean
  onClose: () => void
  /** Sheet title, e.g. "Invoice INV-2026-002". */
  title: string
  /** File name for Download, e.g. "Invoice-INV-2026-002.pdf". */
  fileName: string
  /** Fetches the PDF (with the session). Called each time the sheet opens. */
  load: () => Promise<Blob>
}

/**
 * Whether this browser shows a PDF inside a page. `navigator.pdfViewerEnabled`
 * is false where there is no built-in viewer (Android, some kiosks). iOS and
 * iPadOS report true but only draw the first page as an image, so they get
 * the open/download card too.
 */
export function canShowPdfInline(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> & { pdfViewerEnabled?: boolean } = navigator): boolean {
  const ua = nav.userAgent || ''
  const iOS = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && (nav.maxTouchPoints ?? 0) > 1)
  if (iOS || /Android/i.test(ua)) return false
  return nav.pdfViewerEnabled !== false
}

/** Below this width the browser's own viewer is all chrome (toolbar, thumbnails). */
const FRAME_MIN_WIDTH = 640

/** How the sheet shows the file: the browser's viewer, drawn pages, or a card. */
export type PdfPreviewMode = 'frame' | 'pages' | 'card'

export function pdfPreviewMode(inline = canShowPdfInline(), width = typeof window === 'undefined' ? 1024 : window.innerWidth): PdfPreviewMode {
  return inline && width >= FRAME_MIN_WIDTH ? 'frame' : 'pages'
}

/** A link to the file once it's loaded; a disabled button until then (its real state). */
function FileAction({ href, download, newTab, className, children }: {
  href?: string
  download?: string
  newTab?: boolean
  className: string
  children: React.ReactNode
}) {
  const cls = `${className} inline-flex items-center justify-center gap-2 px-5 whitespace-nowrap`
  if (!href) {
    return <button type="button" className={cls} style={{ minHeight: 44 }} disabled>{children}</button>
  }
  return (
    <a className={cls} style={{ minHeight: 44 }} href={href} download={download} {...(newTab ? { target: '_blank', rel: 'noopener' } : {})}>
      {children}
    </a>
  )
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; url: string; blob: Blob }
  | { kind: 'error'; title: string; message: string; retryable: boolean }

export function PdfPreviewSheet({ open, onClose, title, fileName, load }: PdfPreviewSheetProps) {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const urlRef = useRef<string | null>(null)
  const [mode, setMode] = useState<PdfPreviewMode>(() => pdfPreviewMode())
  const inline = mode === 'frame'

  const release = () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current)
    urlRef.current = null
  }

  const fetchPdf = useCallback(async () => {
    setState({ kind: 'loading' })
    try {
      const blob = await load()
      // The API sends application/pdf; type it explicitly so the viewer
      // never sniffs a generic blob as a download.
      const pdf = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' })
      release()
      urlRef.current = URL.createObjectURL(pdf)
      setState({ kind: 'ready', url: urlRef.current, blob: pdf })
    } catch (err) {
      const d = describeError(err, { action: 'load the PDF', forbiddenWhat: 'see this PDF' })
      setState({ kind: 'error', title: d.title, message: d.message, retryable: d.retryable })
    }
  }, [load])

  useEffect(() => {
    if (!open) return
    setMode(pdfPreviewMode())
    void fetchPdf()
    return release
    // Reload each time the sheet opens; `load` identity changes are ignored
    // while it's open so a parent re-render doesn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const url = state.kind === 'ready' ? state.url : undefined

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      size="full"
      snapPoints={mode === 'card' ? ['auto'] : ['full']}
      footer={
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 w-full">
          {mode !== 'card' && (
            <FileAction className="btn-secondary" href={url} newTab>
              <ExternalLink size={16} aria-hidden />
              {inline ? 'Open in new tab' : 'Open PDF'}
            </FileAction>
          )}
          <FileAction className={mode === 'card' ? 'btn-secondary' : 'btn-primary'} href={url} download={fileName}>
            <Download size={16} aria-hidden />
            Download PDF
          </FileAction>
        </div>
      }
    >
      {state.kind === 'loading' && (
        <div role="status" className="flex flex-col items-center justify-center gap-3 text-center" style={{ minHeight: 240 }}>
          <Spinner />
          <p className="text-sm m-0" style={{ color: 'var(--midnight-text3)' }}>Getting the PDF ready…</p>
        </div>
      )}

      {state.kind === 'error' && (
        <div role="alert" className="ds-tile-inset flex flex-col items-center gap-3 text-center" style={{ padding: 24 }}>
          <AlertCircle size={24} aria-hidden style={{ color: '#FF3B5C' }} />
          <p className="m-0 text-sm" style={{ color: '#FFFFFF', fontWeight: 600 }}>{state.title}</p>
          <p className="m-0 text-sm" style={{ color: 'var(--midnight-text3)' }}>{state.message}</p>
          {state.retryable && (
            <button type="button" className="btn-secondary" style={{ minHeight: 44 }} onClick={() => { void fetchPdf() }}>
              Try again
            </button>
          )}
        </div>
      )}

      {state.kind === 'ready' && inline && (
        <iframe
          title={title}
          src={url}
          className="w-full block"
          style={{ height: 'min(72vh, 900px)', border: 0, borderRadius: 12, background: '#FFFFFF', colorScheme: 'light' }}
        />
      )}

      {state.kind === 'ready' && mode === 'pages' && (
        <PdfPageImages
          blob={state.blob}
          label={title}
          onError={(err) => {
            logger.warn('[PdfPreviewSheet] could not draw the PDF pages; offering Open / Download', err)
            setMode('card')
          }}
        />
      )}

      {state.kind === 'ready' && mode === 'card' && (
        <div className="ds-tile-inset flex flex-col items-center gap-3 text-center" style={{ padding: 24 }}>
          <FileText size={32} aria-hidden style={{ color: 'var(--midnight-text2)' }} />
          <p className="m-0 text-base" style={{ color: '#FFFFFF', fontWeight: 600, overflowWrap: 'anywhere' }}>{fileName}</p>
          <p className="m-0 text-sm" style={{ color: 'var(--midnight-text3)' }}>
            Opens in your phone’s PDF viewer, where you can zoom, share or print it.
          </p>
          <a
            className="btn-primary inline-flex items-center justify-center gap-2 w-full"
            style={{ minHeight: 48 }}
            href={url}
            target="_blank"
            rel="noopener"
          >
            <ExternalLink size={16} aria-hidden />
            Open PDF
          </a>
        </div>
      )}
    </BottomSheet>
  )
}
